import { PlusIcon, TrashIcon } from '@heroicons/react/outline';
import { LockClosedIcon } from '@heroicons/react/solid';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Link, Modal, SkeletonLoader, Toggle } from '@yoast/ui-library';
import type { AxiosResponse } from 'axios';
import cx from 'classnames';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getGetAnalysisQueryKey,
  getGetAnalysisStatusQueryKey,
  getGetBrandQueryKey,
  getGetBrandsQueryKey,
  getGetCurrentUserQueryKey,
  useGetCurrentUser,
  usePostTrialAnalysis,
  useUpdateBrandSettings,
} from '../../api/generated/insights';
import type {
  Brands,
  BrandsBrandsItem,
} from '../../api/generated/insights.schemas';
import {
  useBrandAnalysisData,
  useBrandAnalysisMutations,
} from '../../contexts';
import { useToast } from '../../hooks';
import {
  formatScheduledAnalysisDate,
  isAnalysisScheduledWithinMinutes,
} from '../../utils/timeUtils';

type Props = {
  brand: BrandsBrandsItem;
  isLast: boolean;
  onDelete: (id: string) => void;
};

const SettingsBrandRow: React.FC<Props> = ({ brand, isLast, onDelete }) => {
  const brandId = brand.id;
  const { hostAppProps, analysisId } = useBrandAnalysisData();
  const { onAddSubscription } = hostAppProps;
  const navigate = useNavigate();
  const { setBrandId } = useBrandAnalysisMutations();

  const [showActivationModal, setShowActivationModal] = useState(false);

  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: user, isLoading: isUserLoading } = useGetCurrentUser({
    query: {
      // keep the same select, but force fresh data on mount/window focus/reconnect
      select: ({ data }) => data,
      staleTime: 5 * 1000, // 5 seconds

      // Refetch when the window regains focus or the client reconnects.
      refetchOnWindowFocus: true,

      // refetch interval
      refetchInterval: 30 * 1000, // 30 seconds
    },
  });

  const { mutate: mutateBrandSettings, isPending: isUpdatingBrandSettings } =
    useUpdateBrandSettings<
      unknown,
      { previous?: AxiosResponse<Brands, unknown> }
    >({
      mutation: {
        onMutate: async ({ brandId, data }) => {
          // Cancel outgoing refetches to avoid overwriting our optimistic update
          await queryClient.cancelQueries({ queryKey: getGetBrandsQueryKey() });

          // Snapshot the previous value
          const previous = queryClient.getQueryData<
            AxiosResponse<Brands, unknown>
          >(getGetBrandsQueryKey());

          // Optimistically update the brand settings
          queryClient.setQueryData(
            getGetBrandsQueryKey(),
            (oldData: AxiosResponse<Brands, unknown> | undefined) => {
              if (!oldData) return oldData;

              return {
                ...oldData,
                data: {
                  ...oldData.data,
                  brands: (oldData.data?.brands ?? []).map((b) =>
                    b.id === brandId
                      ? {
                          ...b,
                          settings: {
                            ...b.settings,
                            enableAutomaticAnalysis:
                              data.enableAutomaticAnalysis,
                          },
                        }
                      : b,
                  ),
                },
              } as AxiosResponse<Brands, unknown>;
            },
          );

          // Return context with the previous value for rollback
          return { previous };
        },
        onSuccess: () => {
          if (
            brand &&
            brand.settings &&
            brand.settings.enableAutomaticAnalysis === false
          ) {
            // If we just disabled, close the modal if open and exit
            return setShowActivationModal(false);
          }
          if (!isAnalysisScheduledSoon) {
            // Once successful, show the alert
            setShowActivationModal(true);
          } else {
            // Close the alert once successfully applied
            setShowActivationModal(false);
          }
        },
        onError: (_err, _variables, context) => {
          // Rollback to the previous value if the mutation fails
          if (context?.previous) {
            queryClient.setQueryData(getGetBrandsQueryKey(), context.previous);
          }
        },
        onSettled: async () => {
          // Invalidate brands list to sync with server
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: getGetBrandsQueryKey() }),
            queryClient.invalidateQueries({
              queryKey: getGetCurrentUserQueryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetBrandQueryKey(brandId),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisStatusQueryKey(brandId, 'latest'),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisQueryKey(brandId, 'latest'),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisStatusQueryKey(brandId, analysisId),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisQueryKey(brandId, analysisId),
            }),
          ]);
        },
      },
    });

  const toggleAutomaticAnalysis = (isEnabled: boolean) => {
    // turn on automatic analysis for this brand
    mutateBrandSettings({
      brandId,
      data: {
        enableAutomaticAnalysis: isEnabled,
      },
    });
  };

  const handleToggleBrand = (next: boolean) => {
    if (next) {
      if (isPendingQuerySelection) {
        // brand setup isn't finished yet — prompt the user to complete it.
        return setShowActivationModal(true);
      }
      if (!isSubscriptionAvailable) {
        // show the "need subscription" modal and abort.
        return setShowActivationModal(true);
      }

      if (!isAnalysisScheduledSoon) {
        // go ahead and enable and show alert modal
        // we return here so that the function exits and doesn't continue to the modal logic below
        // the onSuccess will show the alert modal if (!isAnalysisScheduledSoon)
        return toggleAutomaticAnalysis(true);
      }

      // otherwise, show confirmation
      setShowActivationModal(true);
    } else {
      // Turning OFF doesn't need confirmation
      toggleAutomaticAnalysis(false);
    }
  };

  const isSubscriptionAvailable =
    Number(user?.maxNumberOfActiveBrands) - Number(user?.numberOfActiveBrands) >
      0 || !!brand?.settings?.enableAutomaticAnalysis;

  const isAnalysisScheduledSoon = isAnalysisScheduledWithinMinutes(
    brand?.firstNextAnalysisOpportunity,
  );

  const isPendingQuerySelection =
    brand?.setupStatus === 'pendingQuerySelection';

  const isZeroSubscriptions = (user?.activeSubscriptions ?? 0) === 0;
  const isEligibleForFreeTrial = user?.isEligibleForFreeTrial === true;

  const { mutate: startTrialAnalysis, isPending: isStartingTrial } =
    usePostTrialAnalysis({
      mutation: {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: getGetCurrentUserQueryKey(),
            }),
            queryClient.invalidateQueries({ queryKey: getGetBrandsQueryKey() }),
          ]);
          setShowActivationModal(false);
          setBrandId(brandId);
          navigate('/');
        },
        onError: () => {
          toast(
            'Something went wrong starting your free trial. Please try again.',
            { variant: 'error' },
          );
        },
      },
    });

  const activationBlockedVariant = isPendingQuerySelection
    ? {
        title: 'Finish setting up this brand',
        body: "Your brand is saved, but you haven't completed the analysis setup yet. Finalize your settings to start tracking and improving your brand visibility with Yoast AI Brand Insights.",
        buttons: (
          <Button
            variant="primary"
            onClick={() => {
              if (brand.id) {
                setBrandId(brand.id);
                navigate(`/setup/${brand.id}/queries`);
              }
            }}
          >
            Finish setup
          </Button>
        ),
      }
    : !isZeroSubscriptions
      ? {
          title: "You've reached your active brand limit",
          body: 'Each subscription lets you activate 1 brand. Add another subscription or deactivate an existing brand to activate this brand.',
          buttons: (
            <>
              {onAddSubscription && (
                <Button
                  variant="upsell"
                  className="yst-flex yst-gap-1"
                  onClick={onAddSubscription}
                >
                  <PlusIcon />
                  Add a subscription
                </Button>
              )}
              <Button
                variant="tertiary"
                onClick={() => setShowActivationModal(false)}
              >
                Manage active brands
              </Button>
            </>
          ),
        }
      : isEligibleForFreeTrial
        ? {
            title: 'Start your free trial',
            body: 'You have a free trial available. Start it now to activate this brand and begin tracking and improving your brand visibility with Yoast AI Brand Insights.',
            buttons: (
              <Button
                variant="primary"
                onClick={() => startTrialAnalysis({ brandId })}
                isLoading={isStartingTrial}
              >
                Start your free trial
              </Button>
            ),
          }
        : {
            title: 'Your free trial has ended',
            body: 'Upgrade to Yoast SEO AI+ or buy a new subscription to keep using Yoast AI Brand Insights and continue tracking and improving your brand visibility with Yoast AI Brand Insights.',
            buttons: (
              <>
                {onAddSubscription && (
                  <Button
                    variant="upsell"
                    className="yst-flex yst-gap-1"
                    onClick={onAddSubscription}
                  >
                    <PlusIcon />
                    Upgrade my subscription to Yoast SEO AI+
                  </Button>
                )}
                <Button
                  variant="tertiary"
                  onClick={() => setShowActivationModal(false)}
                >
                  Close
                </Button>
              </>
            ),
          };

  return (
    <>
      <div
        className={cx(
          'yst-flex yst-max-w-2xl yst-flex-1 yst-flex-row yst-items-center yst-justify-start yst-gap-4 yst-py-4 yst-text-slate-800',
          {
            'yst-border-b-[1px] yst-border-b-slate-200': !isLast,
          },
        )}
      >
        {/* Brand toggle + name */}
        <Toggle
          id={`toggle-brand-${brand?.id}`}
          checked={brand?.settings?.enableAutomaticAnalysis}
          screenReaderLabel="Enable or disable automatic analysis for this brand"
          onChange={handleToggleBrand}
          disabled={
            brand?.id === 'loading' || isUpdatingBrandSettings || isUserLoading
          }
        />
        <div className="yst-font-medium">
          {brand?.id === 'loading' ? (
            <SkeletonLoader className="yst-h-6 yst-w-32" />
          ) : (
            brand?.name
          )}
          {isPendingQuerySelection && (
            <div>
              <Link
                variant="primary"
                onClick={() => {
                  if (brand.id) {
                    setBrandId(brand.id);
                    navigate(`/setup/${brand.id}/queries`);
                  } else {
                    // TODO: do we need a user-facing error for this case?
                    console.error('Brand ID is missing.');
                  }
                }}
              >
                Finish setup
              </Link>
            </div>
          )}
          {brand?.nextScheduledAnalysis && (
            <div className="yst-font-normal yst-italic yst-text-slate-500">
              Next analysis:{' '}
              {formatScheduledAnalysisDate(brand.nextScheduledAnalysis)}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="yst-ml-auto yst-flex yst-flex-row yst-items-center yst-justify-end yst-gap-2 yst-text-slate-800">
          {brand?.id === 'loading' ? (
            <SkeletonLoader className="yst-h-6 yst-w-24" />
          ) : !brand?.hasAnalysisInProgress ? (
            <Button
              size="small"
              variant="tertiary"
              className="yst-text-red-600 hover:yst-text-red-700"
              onClick={() => {
                if (brand?.id) {
                  onDelete(brand.id);
                }
              }}
            >
              <TrashIcon /> Delete brand
            </Button>
          ) : null}
        </div>
      </div>

      {/* Modal Dialog: Activate brand */}
      <Modal
        isOpen={showActivationModal}
        onClose={() => setShowActivationModal(false)}
        position="center"
        as="div"
        className={cx({
          'yst-mx-auto yst-max-w-xl':
            isSubscriptionAvailable && !isPendingQuerySelection,
        })}
      >
        {isSubscriptionAvailable && !isPendingQuerySelection ? (
          <Modal.Panel>
            {isAnalysisScheduledSoon ? (
              // 1. Show confirmation if analysis is scheduled soon
              <>
                <Modal.Container.Header className="yst-mb-3 yst-w-full">
                  <Modal.Title as="h3" className="yst-text-lg yst-font-medium">
                    Start brand analysis now?
                  </Modal.Title>
                </Modal.Container.Header>
                <Modal.Container.Content className="yst-mb-4 yst-space-y-3">
                  <p>
                    Activating this brand will immediately start a new analysis.
                  </p>
                  <p>
                    This will use one of your weekly analyses included in your
                    subscription.
                  </p>
                </Modal.Container.Content>
                <Modal.Container.Footer className="yst-mt-3 yst-flex yst-justify-end yst-gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => setShowActivationModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => {
                      toggleAutomaticAnalysis(true);
                    }}
                    isLoading={isUpdatingBrandSettings}
                  >
                    Yes, start analysis
                  </Button>
                </Modal.Container.Footer>
              </>
            ) : (
              // 2. Alert user that automatic analysis is enabled
              <>
                <Modal.Container.Header className="yst-mb-3 yst-w-full">
                  <Modal.Title as="h3" className="yst-text-lg yst-font-medium">
                    Automatic analysis enabled
                  </Modal.Title>
                </Modal.Container.Header>
                <Modal.Container.Content className="yst-mb-4 yst-space-y-3">
                  <p>
                    You've turned on the weekly automatic analysis for this
                    brand.
                  </p>
                  <p>
                    The next analysis will run on{' '}
                    <span className="yst-font-medium">
                      {formatScheduledAnalysisDate(
                        brand?.nextScheduledAnalysis,
                      ) ||
                        // fallback if analysis queue failed to trigger
                        'the next analysis cycle'}
                    </span>
                    .
                  </p>
                </Modal.Container.Content>
                <Modal.Container.Footer className="yst-mt-3 yst-flex yst-justify-end yst-gap-2">
                  <Button
                    variant="primary"
                    onClick={() => setShowActivationModal(false)}
                  >
                    Got it
                  </Button>
                </Modal.Container.Footer>
              </>
            )}
          </Modal.Panel>
        ) : (
          <Modal.Panel className="yst-flex yst-max-w-96 yst-flex-col yst-gap-6">
            <div className="yst-flex yst-justify-center">
              <div className="yst-flex yst-h-12 yst-w-12 yst-items-center yst-justify-center yst-rounded-full yst-bg-amber-200">
                <LockClosedIcon className="yst-h-6 yst-w-6 yst-text-amber-900" />
              </div>
            </div>
            <div className="yst-flex yst-items-start yst-justify-stretch yst-gap-4">
              <div className="yst-flex-1">
                <Modal.Container.Header className="yst-mb-3 yst-w-full">
                  <Modal.Title
                    as="h3"
                    className="yst-text-center yst-text-lg yst-font-medium"
                  >
                    {activationBlockedVariant.title}
                  </Modal.Title>
                </Modal.Container.Header>
                <Modal.Container.Content>
                  <p className="yst-text-center">{activationBlockedVariant.body}</p>
                </Modal.Container.Content>
              </div>
            </div>
            <Modal.Container.Footer>
              <div className="yst-flex yst-flex-col yst-justify-stretch yst-gap-2">
                {activationBlockedVariant.buttons}
              </div>
            </Modal.Container.Footer>
          </Modal.Panel>
        )}
      </Modal>
    </>
  );
};

export default SettingsBrandRow;
